import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import { useCurrency, Currency, CURRENCY_SYMBOLS } from '../context/CurrencyContext';
import { API_ENDPOINTS } from '../utils/config';
import '../styles/StatsCards.css';

interface StatCardProps {
  title: string;
  value: string;
  change: string;
  changeType: 'positive' | 'negative' | 'neutral';
}

const StatCard: React.FC<StatCardProps> = ({ title, value, change, changeType }) => {
  return (
    <div className="stat-card">
      <h3 className="stat-title">{title}</h3>
      <div className="stat-value">{value}</div>
      <div className={`stat-change ${changeType}`}>
        {changeType !== 'neutral' && (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            {changeType === 'positive' ? (
              <path d="M8 12L12 8L8 4M4 8H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            ) : (
              <path d="M8 4L4 8L8 12M12 8H4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            )}
          </svg>
        )}
        {change}
      </div>
    </div>
  );
};

interface CompanySavings {
  total_savings: number;
  current_month_savings: number;
  previous_month_savings: number;
  month_over_month_change: number;
  currency?: string;
}

interface CompanyTotalSpendPipeline {
  total_spend_pipeline: number;
  current_month_spend: number;
  previous_month_spend: number;
  month_over_month_change: number;
  currency: string;
}

interface CompanyCommittedSpend {
  committed_spend: number;
  current_month_spend: number;
  previous_month_spend: number;
  month_over_month_change: number;
  currency: string;
}

const StatsCards: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { selectedCurrency, currencySymbol, isLoading, setIsLoading } = useCurrency();
  const [savings, setSavings] = useState<CompanySavings | null>(null);
  const [totalSpendPipeline, setTotalSpendPipeline] = useState<CompanyTotalSpendPipeline | null>(null);
  const [committedSpend, setCommittedSpend] = useState<CompanyCommittedSpend | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        if (!user?.company?._id) return;

        const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
        if (!token) return;

        console.log('🔍 StatsCards - Fetching data for company:', user.company._id);

        // First, fetch debug info to see what requests exist
        try {
          const debugResponse = await fetch(API_ENDPOINTS.COMPANY_REQUESTS_DEBUG(user.company._id), {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          if (debugResponse.ok) {
            const debugResult = await debugResponse.json();
            console.log('🔍 Debug info:', debugResult);
          }
        } catch (debugError) {
          console.error('Debug fetch failed:', debugError);
        }

        // Fetch all three data points in parallel with selected currency
        const [savingsResponse, pipelineResponse, committedResponse] = await Promise.all([
          fetch(`${API_ENDPOINTS.COMPANY_SAVINGS(user.company._id)}?currency=${selectedCurrency}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }),
          fetch(`${API_ENDPOINTS.COMPANY_TOTAL_SPEND_PIPELINE(user.company._id)}?currency=${selectedCurrency}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }),
          fetch(`${API_ENDPOINTS.COMPANY_COMMITTED_SPEND(user.company._id)}?currency=${selectedCurrency}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })
        ]);

        // Process savings response
        if (savingsResponse.ok) {
          const result = await savingsResponse.json();
          if (result.success && result.data) {
            setSavings(result.data);
          }
        }

        // Process pipeline response
        if (pipelineResponse.ok) {
          const result = await pipelineResponse.json();
          console.log('🔍 Pipeline response:', result);
          if (result.success && result.data) {
            setTotalSpendPipeline(result.data);
          }
        } else {
          console.error('🔍 Pipeline response failed:', pipelineResponse.status, await pipelineResponse.text());
        }

        // Process committed spend response
        console.log('🔍 Committed spend response status:', committedResponse.status, committedResponse.ok);
        if (committedResponse.ok) {
          const result = await committedResponse.json();
          console.log('🔍 Committed spend response:', result);
          console.log('🔍 Committed spend result.success:', result.success);
          console.log('🔍 Committed spend result.data:', result.data);
          if (result.success && result.data) {
            console.log('🔍 Setting committed spend data:', result.data);
            setCommittedSpend(result.data);
          } else {
            console.log('🔍 Committed spend data not set - success:', result.success, 'data exists:', !!result.data);
          }
        } else {
          console.error('🔍 Committed spend response failed:', committedResponse.status, await committedResponse.text());
        }

      } catch (error) {
        console.error('Error fetching company data:', error);
      } finally {
        setLoading(false);
        // Stop currency loading when data is fetched
        if (isLoading) {
          setIsLoading(false);
        }
      }
    };

    // Clear data when currency changes and loading starts
    if (isLoading) {
      setTotalSpendPipeline(null);
      setCommittedSpend(null);
    }

    // Fetch data for initial load or when currency changes
    fetchCompanyData();
  }, [user?.company?._id, selectedCurrency, isLoading]);

  const formatCurrency = (amount: number, currency: string = selectedCurrency) => {
    // Round TRY amounts to nearest whole number (no cents)
    let formattedAmount = amount;
    if (currency === 'TRY') {
      formattedAmount = Math.round(amount);
    }
    
    // Use currencySymbol from context instead of Intl.NumberFormat for consistent symbols
    const symbol = currency === selectedCurrency ? currencySymbol : CURRENCY_SYMBOLS[currency as Currency] || currency;
    return `${symbol}${formattedAmount.toLocaleString()}`;
  };

  const formatPercentage = (percentage: number) => {
    // If percentage is 100, it means only one month of data exists, don't show percentage
    if (percentage === 100) {
      return '';
    }
    const sign = percentage >= 0 ? '+' : '';
    return `${sign}${percentage}% ${t('common.month_over_month')}`;
  };

  // Calculate savings percentage for the savings card
  const calculateSavingsPercentage = () => {
    if (!savings || !committedSpend || committedSpend.committed_spend === 0) {
      return null;
    }
    
    // Convert both to same currency for comparison
    const savingsAmount = savings.total_savings;
    const committedAmount = committedSpend.committed_spend;
    
    // Calculate percentage: (savings / committed_spend) * 100
    const percentage = (savingsAmount / committedAmount) * 100;
    return Math.round(percentage * 10) / 10; // Round to 1 decimal place
  };

  const savingsPercentage = calculateSavingsPercentage();

  const stats = [
    {
      title: t('common.total_spend_in_pipeline'),
      value: totalSpendPipeline ? formatCurrency(totalSpendPipeline.total_spend_pipeline, totalSpendPipeline.currency) : (isLoading ? '' : formatCurrency(0, selectedCurrency)),
      change: totalSpendPipeline ? formatPercentage(totalSpendPipeline.month_over_month_change) : (isLoading ? '' : `0% ${t('common.month_over_month')}`),
      changeType: totalSpendPipeline && totalSpendPipeline.month_over_month_change !== 100 ? 
        ((totalSpendPipeline.month_over_month_change || 0) >= 0 ? "positive" as const : "negative" as const) : 
        "neutral" as const
    },
    {
      title: t('common.committed_spend'),
      value: committedSpend ? formatCurrency(committedSpend.committed_spend, committedSpend.currency) : (isLoading ? '' : formatCurrency(0, selectedCurrency)),
      change: committedSpend ? formatPercentage(committedSpend.month_over_month_change) : (isLoading ? '' : `0% ${t('common.month_over_month')}`),
      changeType: committedSpend && committedSpend.month_over_month_change !== 100 ? 
        ((committedSpend.month_over_month_change || 0) >= 0 ? "positive" as const : "negative" as const) : 
        "neutral" as const
    },
    {
      title: t('common.expected_savings'),
      value: savings ? formatCurrency(savings.total_savings, savings.currency || selectedCurrency) : (isLoading ? '' : formatCurrency(0, selectedCurrency)),
      change: savingsPercentage !== null ? `${savingsPercentage}% ${t('common.savings_rate')}` : (isLoading ? '' : `0% ${t('common.savings_rate')}`),
      changeType: savingsPercentage !== null ? "positive" as const : "neutral" as const
    }
  ];

  console.log('🔍 StatsCards - Current data state:', { savings, totalSpendPipeline, committedSpend });
  console.log('🔍 StatsCards - Stats array:', stats);
  console.log('🔍 StatsCards - User object:', user);
  console.log('🔍 StatsCards - Company details:', user?.company);

  // Show loading for initial load or currency changing
  if (loading || isLoading) {
    return (
      <div className="stats-cards">
        {[1, 2, 3].map((index) => (
          <div key={index} className="stat-card loading">
            <div className="stat-title">{t('common.loading')}</div>
            <div className="stat-value">
              <div className="loading-skeleton"></div>
            </div>
            <div className="stat-change">
              <div className="loading-skeleton-small"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="stats-cards">
      {stats.map((stat, index) => (
        <StatCard
          key={index}
          title={stat.title}
          value={stat.value}
          change={stat.change}
          changeType={stat.changeType}
        />
      ))}
    </div>
  );
};

export default StatsCards; 